import { EMPTY_BITRATE_CHART, type BitrateChartData } from "../utils/bitrateHistory";
import { useMemo } from "react";
import { Grid } from "@mui/material";
import { type StreamStateValue } from "./StreamState";
import StreamCard from "./StreamCard";
import type { StreamPreviewPopoverOpenAnimation } from "./StreamPreviewPopover";
import { useStreamPreviewCollection } from "../hooks/useStreamPreviewCollection";
import type { StreamSummary } from "../types/streamSummary";
import { type StreamOutputStatsSnapshot } from "../utils/streamOutputs";
import type { FileSourceInfoSnapshot } from "../types/fileSourceInfo";

type StreamCardsProps = {
  streams: StreamSummary[];
  canManageStreams: boolean;
  canViewDetectionInfo: boolean;
  commandsInFlight: Record<string, "start" | "stop">;
  streamBitratesById: Map<string, BitrateChartData>;
  fileSourceInfoByStream: Map<string, FileSourceInfoSnapshot>;
  onCommand: (args: { streamId: string; command: "start" | "stop" }) => void;
  onOpenEdit: (stream: StreamSummary) => void;
  onOpenDelete: (stream: StreamSummary) => void;
  onOpenDetection: (stream: StreamSummary) => void;
  onToggleTimeline: (stream: StreamSummary) => void;
  onToggleCalendar: (stream: StreamSummary) => void;
  onOpenPlayer: (stream: StreamSummary) => void;
  timelineStreamId: string | null;
  calendarStreamId: string | null;
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  getStreamStateMessage: (streamId: string) => unknown;
  getOutputState: (streamId: string, outputId: string) => StreamStateValue | undefined;
  getOutputStateMessage: (streamId: string, outputId: string) => string | null;
  outputStatsByStream: Map<string, Map<string, StreamOutputStatsSnapshot>>;
  previewOpenAnimation?: StreamPreviewPopoverOpenAnimation;
};

const StreamCards = ({
  streams,
  canManageStreams,
  canViewDetectionInfo,
  commandsInFlight,
  streamBitratesById,
  fileSourceInfoByStream,
  onCommand,
  onOpenEdit,
  onOpenDelete,
  onOpenDetection,
  onToggleTimeline,
  onToggleCalendar,
  onOpenPlayer,
  timelineStreamId,
  calendarStreamId,
  getStreamState,
  getStreamStateMessage,
  getOutputState,
  getOutputStateMessage,
  outputStatsByStream,
  previewOpenAnimation = "none"
}: StreamCardsProps) => {
  const streamIds = useMemo(() => streams.map((stream) => stream.id), [streams]);
  const { previewUrls } = useStreamPreviewCollection({
    streamIds,
    getStreamState,
    getStreamStateMessage
  });

  if (!streams.length) {
    return null;
  }

  return (
    <Grid container spacing={2} alignItems="stretch">
      {streams.map((stream) => {
        const pendingCommand = commandsInFlight[stream.id];
        const isTimelineSelected = timelineStreamId === stream.id;
        const isCalendarSelected = calendarStreamId === stream.id;
        const bitrates = streamBitratesById.get(stream.id) ?? EMPTY_BITRATE_CHART;
        const fileSourceInfo = fileSourceInfoByStream.get(stream.id);
        const streamOutputStats = outputStatsByStream.get(stream.id);

        return (
          <Grid key={stream.id} size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex" }}>
            <StreamCard
              stream={stream}
              canManageStreams={canManageStreams}
              canViewDetectionInfo={canViewDetectionInfo}
              pendingCommand={pendingCommand}
              isTimelineSelected={isTimelineSelected}
              isCalendarSelected={isCalendarSelected}
              bitrates={bitrates}
              previewUrl={previewUrls[stream.id]}
              fileSourceInfo={fileSourceInfo}
              streamOutputStats={streamOutputStats}
              onCommand={onCommand}
              onOpenEdit={onOpenEdit}
              onOpenDelete={onOpenDelete}
              onOpenDetection={onOpenDetection}
              onToggleTimeline={onToggleTimeline}
              onToggleCalendar={onToggleCalendar}
              onOpenPlayer={onOpenPlayer}
              getStreamState={getStreamState}
              getStreamStateMessage={getStreamStateMessage}
              getOutputState={getOutputState}
              getOutputStateMessage={getOutputStateMessage}
              previewOpenAnimation={previewOpenAnimation}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default StreamCards;
